import { useEffect, useMemo, useState } from 'react';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

const toSlug = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Next upcoming free challenge (public session group) from the education DB.
 * Fail-soft: returns null when the catalog has no future public run.
 *
 * @returns {{
 *   challenge: {
 *     id: string,
 *     name: string,
 *     description: string | null,
 *     courseTitle: string | null,
 *     kickoffAt: Date | null,
 *     slug: string,
 *     path: string,
 *   } | null,
 *   isLoading: boolean,
 *   kickoffLabel: string | null,
 * }}
 */
const useNextPublicChallenge = () => {
  const [challenge, setChallenge] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // A failed read is not "nothing is scheduled". Callers must fail loudly.
  const [loadFailed, setLoadFailed] = useState(false);
  // True when the catalog holds any public run at all, including one whose
  // kickoff already passed. /challenges must still render the registration
  // page for that run (it shows the started-state recovery); only a genuinely
  // empty catalog may show the "being scheduled" fallback.
  const [hasPublicRun, setHasPublicRun] = useState(false);

  useEffect(() => {
    let mounted = true;
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';

    (async () => {
      try {
        // Ask the API for public registration runs only. Older entitled cohorts
        // otherwise bury challenges behind a large page of test/orphan rows.
        const res = await fetch(
          `${API_URL}/courses/session-groups?active=true&registrationAccess=public&limit=50`,
        );
        if (!res.ok) throw new Error(`session-groups ${res.status}`);
        const data = await res.json();
        const groups = Array.isArray(data) ? data : data.sessionGroups || data.data || [];
        const now = Date.now();
        const publicRuns = groups.filter((group) => group?.registrationAccess === 'public');
        if (mounted) setHasPublicRun(publicRuns.length > 0);

        const next =
          groups
            .filter(
              (group) =>
                group?.registrationAccess === 'public' &&
                group?.scheduledAt &&
                new Date(group.scheduledAt).getTime() > now,
            )
            .sort(
              (a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime(),
            )[0] || null;

        if (!mounted) return;
        setLoadFailed(false);
        if (!next) {
          setChallenge(null);
          return;
        }

        const kickoff = next.nextSession?.scheduledAt || next.scheduledAt;
        setChallenge({
          id: next.id,
          name: next.name,
          description: next.description || next.course?.title || null,
          courseTitle: next.course?.title || null,
          kickoffAt: kickoff ? new Date(kickoff) : null,
          slug: toSlug(next.name),
          // Rob, 2026-09-11: never link to a specific run. /challenges renders
          // the current run in place.
          path: '/challenges',
        });
      } catch (error) {
        if (mounted) {
          setChallenge(null);
          setHasPublicRun(false);
          setLoadFailed(true);
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const formatKickoff = useMemo(
    () => (value) => {
      if (!value) return null;
      return new Date(value).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone: 'America/New_York',
      });
    },
    [],
  );

  return {
    challenge,
    isLoading,
    loadFailed,
    hasPublicRun,
    kickoffLabel: challenge?.kickoffAt ? formatKickoff(challenge.kickoffAt) : null,
  };
};

export default useNextPublicChallenge;
